/**
 * Legacy `SettingsSection` wrapper — used by the deep-route pages
 * (EventMappingPage, AuditLogPage) that lived under the old
 * SettingsLayout-with-Outlet structure. The new tab-based layout
 * uses StSectionHead from tabs/_shared.tsx instead, but these two
 * deep pages don't sit inside the tab strip so they keep their
 * own self-contained header.
 */

import type { ReactNode } from "react";
import { useAuth } from "@/lib/useAuth";

export function SettingsSection({
  title,
  description,
  actions,
  orgScoped = true,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  orgScoped?: boolean;
  children: ReactNode;
}) {
  const { user } = useAuth();
  const showOrgBadge =
    orgScoped && user != null && (user.orgs?.length ?? 0) > 1 && !!user.org_name;

  return (
    <div>
      {showOrgBadge && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            marginBottom: 10,
            borderRadius: 999,
            background: "var(--c-surface-sunk)",
            border: "1px solid var(--c-border)",
            fontSize: 11.5,
            color: "var(--c-text-2)",
          }}
        >
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "var(--c-accent-500, #4a7cff)",
            }}
          />
          <span className="muted" style={{ fontSize: 11 }}>for</span>
          <span style={{ fontWeight: 500, color: "var(--c-text)" }}>
            {user!.org_name}
          </span>
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 4,
        }}
      >
        <h1
          className="serif"
          style={{ margin: 0, fontSize: 32, fontWeight: 400, letterSpacing: "-0.015em" }}
        >
          {title}
        </h1>
        {actions && <div style={{ display: "flex", gap: 8 }}>{actions}</div>}
      </div>
      {description && (
        <p
          className="muted"
          style={{ margin: "6px 0 24px", fontSize: 13.5, maxWidth: 620 }}
        >
          {description}
        </p>
      )}
      {children}
    </div>
  );
}
